// src/Components/ImportMarkers/CoordinateMatchDialog.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMapInitialView } from '../../Hooks/useMapInitialView';
import CoordinateMatchContent from './CoordinateMatchContent';

const normalizeString = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/[\s,]+/g, '')
    .toLowerCase();
};

const normalizeHtmlForCompare = (html, context = '') => {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');

    // Explicit entity decoding to align Tiptap's output with raw Excel data
    let cleaned = String(html)
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ');

    // Strip zero-width spaces and BOM characters
    cleaned = cleaned.replace(/[\u200B-\u200D\uFEFF]/g, '');

    const text = doc.body.textContent.replace(/[\u200B-\u200D\uFEFF]/g, '') || '';

    // Prevent Tiptap's auto-injected empty formatting spans from triggering a save
    if (text.trim() === '' && !doc.querySelector('img, video, audio')) {
      return '';
    }

    // Standardize raw text to <p>
    if (!/<\/?(p|br|div|h[1-6]|ul|ol|li|table|blockquote)[^>]*>/i.test(cleaned)) {
      cleaned = cleaned.replace(/\r?\n/g, '<br>');
      cleaned = `<p>${cleaned}</p>`;
    }

    // Clean out specific structural noise Tiptap alters or drops
    cleaned = cleaned.replace(/<p><\/p>/gi, '');
    cleaned = cleaned.replace(/margin-bottom:\s*[^;"]+;?/gi, '');
    cleaned = cleaned.replace(/margin-top:\s*[^;"]+;?/gi, '');
    cleaned = cleaned.replace(/line-height:\s*[^;"]+;?/gi, '');

    // Remove empty style tags left behind by the above replacements
    cleaned = cleaned.replace(/style="\s*"/gi, '');
    cleaned = cleaned.replace(/style='\s*'/gi, '');
    cleaned = cleaned.replace(/\/>/g, '>'); // Normalize self-closing tags

    // Final hash: Remove all spaces, semicolons, and quotes
    cleaned = cleaned.replace(/[\s;"']/g, '');

    return cleaned;
  } catch (e) {
    return String(html)
      .trim()
      .replace(/[\s;"']/g, '');
  }
};

const resolveCoordsRobustly = (row, initLat, initLng, mappings, custom) => {
  let lat = row?.lat;
  let lng = row?.lng;

  if (lat === undefined || lat === '') {
    lat =
      initLat ??
      row?.['Latitude'] ??
      row?.['latitude'] ??
      row?.['Lat'] ??
      row?.['lat'] ??
      row?.['LAT'] ??
      row?.['LATITUDE'];
  }
  if (lng === undefined || lng === '') {
    lng =
      initLng ??
      row?.['Longitude'] ??
      row?.['longitude'] ??
      row?.['Lng'] ??
      row?.['lng'] ??
      row?.['LNG'] ??
      row?.['LONGITUDE'] ??
      row?.['Lon'] ??
      row?.['lon'];
  }

  if ((!lat || !lng) && mappings && custom) {
    const posColKey = Object.keys(mappings).find(
      (k) => mappings[k] === 'POSITION (LAT, LNG)' || mappings[k] === 'POSITION',
    );
    if (posColKey) {
      let posColName = null;
      const cRow = custom.find((r) => r.id === posColKey);
      if (cRow && cRow.name !== '-') posColName = cRow.name;
      else if (posColKey.startsWith('prop_')) posColName = posColKey.substring(5);

      if (posColName && row?.[posColName]) {
        const matches = String(row[posColName]).match(/-?\d+(\.\d+)?/g);
        if (matches && matches.length >= 2) {
          lat = matches[0];
          lng = matches[1];
        }
      }
    }
  }

  return { lat, lng };
};

export default function CoordinateMatchDialog({
  open,
  isCoordsActive,
  isManualMatchActive,
  isTypeDesc,
  onConfirm,
  onClose,
  onUpdatePhotos,
  onUpdateRowDescription,
  onCancelBulk,
  rowData,
  searchTarget,
  actualTitle,
  geoSuffix,
  initialLat,
  initialLng,
  makeUppercase,
  rteFontType,
  rteFontSize,
  rowMappings,
  rowSubMappings,
  customRows,
  isBulkGenerating,
  userZoomLevel,
  onZoomChange,
  children,
}) {
  const [localTitle, setLocalTitle] = useState('');
  const [localSearchTarget, setLocalSearchTarget] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [applyToAll, setApplyToAll] = useState(false);

  const [isOkPressed, setIsOkPressed] = useState(false);
  const [draggedIdx, setDraggedIdx] = useState(null);

  const [selectedDescPhotoIdx, setSelectedDescPhotoIdx] = useState(0);
  const [photoDescriptions, setPhotoDescriptions] = useState({});
  const prevDescRowIdRef = useRef(null);

  const seededDescMapRef = useRef({});
  const lastSavedDescMapRef = useRef({});

  const [isBulkScraping, setIsBulkScraping] = useState(false);
  const [bulkWaterfallPhotos, setBulkWaterfallPhotos] = useState([]);
  const waterfallRef = useRef(null);

  const [isBulkDescActive, setIsBulkDescActive] = useState(false);
  const [bulkCompletedRow, setBulkCompletedRow] = useState(null);

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const lastRowIdRef = useRef(null);

  const submitRef = useRef(null);
  const cancelRef = useRef(null);
  const navPrevRef = useRef(null);
  const navNextRef = useRef(null);
  const navUpRef = useRef(null);
  const navDownRef = useRef(null);
  const enterOriginRef = useRef(null);

  const lastGridCellRef = useRef(null);
  const spaceTriggeredRef = useRef(false);

  useEffect(() => {
    const trackFocus = (e) => {
      if (e.target && e.target.closest) {
        const cell = e.target.closest(
          'td, th, tr, [role="cell"], [role="gridcell"], [role="row"], .MuiDataGrid-cell, .MuiDataGrid-row',
        );
        if (cell) {
          lastGridCellRef.current = cell;
        }
      }
    };
    window.addEventListener('focusin', trackFocus);
    return () => window.removeEventListener('focusin', trackFocus);
  }, []);

  const restoreGridFocus = useCallback(() => {
    const activeEl = document.activeElement;
    if (activeEl && activeEl.tagName !== 'BODY') {
      activeEl.blur();
    }

    setTimeout(() => {
      let focused = false;

      if (lastGridCellRef.current && document.contains(lastGridCellRef.current)) {
        lastGridCellRef.current.focus({ preventScroll: true });
        const mouseOpts = {
          bubbles: true,
          cancelable: true,
          view: window,
          button: 0,
          buttons: 1,
        };
        lastGridCellRef.current.dispatchEvent(new MouseEvent('mousedown', mouseOpts));
        focused = true;
      }

      if (!focused) {
        const grid = document.querySelector('.MuiDataGrid-root, [role="grid"]');
        if (grid) {
          grid.focus({ preventScroll: true });
          focused = true;
        }
      }

      if (!focused) {
        document.body.focus();
      }
    }, 150);
  }, []);

  const { viewConfig } = useMapInitialView();

  const userZoomRef = useRef(userZoomLevel);
  useEffect(() => {
    userZoomRef.current = userZoomLevel;
  }, [userZoomLevel]);

  const onZoomChangeRef = useRef(onZoomChange);
  useEffect(() => {
    onZoomChangeRef.current = onZoomChange;
  }, [onZoomChange]);

  useEffect(() => {
    const handleBulkStart = () => {
      setIsBulkScraping(true);
      setBulkWaterfallPhotos([]);
    };

    const handleBulkAdd = (e) => {
      if (e.detail) {
        setBulkWaterfallPhotos((prev) => [...prev, e.detail]);
      }
    };

    const handleBulkEnd = () => {
      setIsBulkScraping(false);
      setBulkWaterfallPhotos([]);
    };

    const handleDescStart = () => {
      setIsBulkDescActive(true);
      setBulkCompletedRow(null);
    };

    const handleDescUpdate = (e) => {
      setBulkCompletedRow(e.detail);
    };

    const handleDescEnd = () => {
      setIsBulkDescActive(false);
      setBulkCompletedRow(null);
    };

    window.addEventListener('loktr-bulk-scrape-start', handleBulkStart);
    window.addEventListener('loktr-bulk-scrape-add', handleBulkAdd);
    window.addEventListener('loktr-bulk-scrape-end', handleBulkEnd);
    window.addEventListener('loktr-bulk-desc-start', handleDescStart);
    window.addEventListener('loktr-bulk-desc-update', handleDescUpdate);
    window.addEventListener('loktr-bulk-desc-end', handleDescEnd);

    return () => {
      window.removeEventListener('loktr-bulk-scrape-start', handleBulkStart);
      window.removeEventListener('loktr-bulk-scrape-add', handleBulkAdd);
      window.removeEventListener('loktr-bulk-scrape-end', handleBulkEnd);
      window.removeEventListener('loktr-bulk-desc-start', handleDescStart);
      window.removeEventListener('loktr-bulk-desc-update', handleDescUpdate);
      window.removeEventListener('loktr-bulk-desc-end', handleDescEnd);
    };
  }, []);

  const activePanelData =
    isBulkDescActive && bulkCompletedRow ? bulkCompletedRow : rowData;

  const activePanelDataRef = useRef(activePanelData);
  const photoDescriptionsRef = useRef(photoDescriptions);
  const isTypeDescRef = useRef(isTypeDesc);
  const isBulkDescActiveRef = useRef(isBulkDescActive);
  const rowMappingsRef = useRef(rowMappings);
  const customRowsRef = useRef(customRows);
  const onUpdatePhotosRef = useRef(onUpdatePhotos);
  const onUpdateRowDescriptionRef = useRef(onUpdateRowDescription);

  useEffect(() => {
    activePanelDataRef.current = activePanelData;
    photoDescriptionsRef.current = photoDescriptions;
    isTypeDescRef.current = isTypeDesc;
    isBulkDescActiveRef.current = isBulkDescActive;
    rowMappingsRef.current = rowMappings;
    customRowsRef.current = customRows;
    onUpdatePhotosRef.current = onUpdatePhotos;
    onUpdateRowDescriptionRef.current = onUpdateRowDescription;
  });

  const flushSave = useCallback(() => {
    if (
      !isTypeDescRef.current ||
      isBulkDescActiveRef.current ||
      !activePanelDataRef.current
    )
      return;

    const currentData = activePanelDataRef.current;
    const currentDescs = photoDescriptionsRef.current;

    // Prevent saving if photoDescriptions has not yet synced to the currently active row
    if (
      prevDescRowIdRef.current !== currentData.id ||
      (currentDescs._rowId && currentDescs._rowId !== currentData.id)
    ) {
      return;
    }

    let changed = false;

    let descColName = null;
    const descColKey = Object.keys(rowMappingsRef.current || {}).find(
      (k) => rowMappingsRef.current[k] === 'DESCRIPTION',
    );
    if (descColKey) {
      const cRow = customRowsRef.current?.find((r) => r.id === descColKey);
      if (cRow && cRow.name !== '-') descColName = cRow.name;
      else if (descColKey.startsWith('prop_')) descColName = descColKey.substring(5);
    }

    let actualKey = null;
    if (descColName) {
      actualKey =
        Object.keys(currentData).find(
          (k) => normalizeString(k) === normalizeString(descColName),
        ) || descColName;
    }

    if (currentData.matchedFiles && currentData.matchedFiles.length > 0) {
      const newFiles = [...currentData.matchedFiles];
      let rowDescChanged = false;
      let newRowDesc = '';

      newFiles.forEach((f, idx) => {
        const localDesc = currentDescs[idx] || '';
        let existingDesc = f.description;
        if (
          existingDesc === undefined &&
          idx === 0 &&
          actualKey &&
          currentData[actualKey]
        ) {
          existingDesc = currentData[actualKey];
        }
        existingDesc = existingDesc || '';
        const seededDesc = seededDescMapRef.current[idx] || '';

        const normLocal = normalizeHtmlForCompare(localDesc, 'LOCAL');
        const normExisting = normalizeHtmlForCompare(existingDesc, 'EXISTING');
        const normSeeded = normalizeHtmlForCompare(seededDesc, 'SEEDED');

        if (normLocal !== normExisting && normLocal !== normSeeded) {
          f.description = localDesc;
          lastSavedDescMapRef.current[idx] = localDesc;
          changed = true;
          if (idx === 0) {
            rowDescChanged = true;
            newRowDesc = localDesc;
          }
        }
      });

      if (changed && onUpdatePhotosRef.current) {
        onUpdatePhotosRef.current(newFiles, actualKey);
      }
      if (rowDescChanged && actualKey && onUpdateRowDescriptionRef.current) {
        onUpdateRowDescriptionRef.current(currentData.id, actualKey, newRowDesc);
      }
    } else {
      const localDesc = currentDescs[0] || '';
      if (actualKey) {
        const existingDesc = currentData[actualKey] || '';
        const seededDesc = seededDescMapRef.current[0] || '';

        const normLocal = normalizeHtmlForCompare(localDesc, 'LOCAL');
        const normExisting = normalizeHtmlForCompare(existingDesc, 'EXISTING');
        const normSeeded = normalizeHtmlForCompare(seededDesc, 'SEEDED');

        if (normLocal !== normExisting && normLocal !== normSeeded) {
          lastSavedDescMapRef.current[0] = localDesc;
          if (onUpdateRowDescriptionRef.current) {
            onUpdateRowDescriptionRef.current(currentData.id, actualKey, localDesc);
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    if (waterfallRef.current) {
      waterfallRef.current.scrollTo({
        top: waterfallRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [bulkWaterfallPhotos]);

  useEffect(() => {
    if (open && isTypeDesc && activePanelData?.id) {
      if (prevDescRowIdRef.current !== activePanelData.id) {
        prevDescRowIdRef.current = activePanelData.id;
        setSelectedDescPhotoIdx(0);
        lastSavedDescMapRef.current = {};
        const initialDescMap = { _rowId: activePanelData.id };

        let hasAnyDescProp = false;
        if (activePanelData.matchedFiles && activePanelData.matchedFiles.length > 0) {
          activePanelData.matchedFiles.forEach((file, idx) => {
            if (file.description !== undefined) {
              hasAnyDescProp = true;
              if (file.description) initialDescMap[idx] = file.description;
            }
          });
        }

        if (!hasAnyDescProp) {
          let descColName = null;
          const descColKey = Object.keys(rowMappings || {}).find(
            (k) => rowMappings[k] === 'DESCRIPTION',
          );
          if (descColKey) {
            const cRow = customRows?.find((r) => r.id === descColKey);
            if (cRow && cRow.name !== '-') descColName = cRow.name;
            else if (descColKey.startsWith('prop_'))
              descColName = descColKey.substring(5);
          }
          if (descColName) {
            const actualKey =
              Object.keys(activePanelData).find(
                (k) => normalizeString(k) === normalizeString(descColName),
              ) || descColName;
            if (activePanelData[actualKey]) {
              initialDescMap[0] = activePanelData[actualKey];
            }
          }
        }

        seededDescMapRef.current = { ...initialDescMap };
        setPhotoDescriptions(initialDescMap);
      } else {
        setPhotoDescriptions((prev) => {
          const newMap = { ...prev, _rowId: activePanelData.id };
          let changed = false;

          let descColName = null;
          const descColKey = Object.keys(rowMappings || {}).find(
            (k) => rowMappings[k] === 'DESCRIPTION',
          );
          if (descColKey) {
            const cRow = customRows?.find((r) => r.id === descColKey);
            if (cRow && cRow.name !== '-') descColName = cRow.name;
            else if (descColKey.startsWith('prop_'))
              descColName = descColKey.substring(5);
          }

          let actualKey = null;
          if (descColName) {
            actualKey =
              Object.keys(activePanelData).find(
                (k) => normalizeString(k) === normalizeString(descColName),
              ) || descColName;
          }

          let hasAnyIncomingDescProp = false;
          if (activePanelData.matchedFiles && activePanelData.matchedFiles.length > 0) {
            activePanelData.matchedFiles.forEach((file) => {
              if (file.description !== undefined) hasAnyIncomingDescProp = true;
            });
          }

          if (hasAnyIncomingDescProp) {
            activePanelData.matchedFiles.forEach((file, idx) => {
              let incomingDesc = file.description;

              if (
                incomingDesc === undefined &&
                idx === 0 &&
                actualKey &&
                activePanelData[actualKey]
              ) {
                incomingDesc = activePanelData[actualKey];
              }
              incomingDesc = incomingDesc || '';

              if (incomingDesc === lastSavedDescMapRef.current[idx]) {
                return;
              }

              const localDesc = prev[idx] || '';
              if (incomingDesc && incomingDesc !== localDesc) {
                newMap[idx] = incomingDesc;
                changed = true;
              }
            });
          } else {
            if (actualKey) {
              const incomingDesc = activePanelData[actualKey] || '';

              if (incomingDesc !== lastSavedDescMapRef.current[0]) {
                const localDesc = prev[0] || '';
                if (incomingDesc && incomingDesc !== localDesc) {
                  newMap[0] = incomingDesc;
                  changed = true;
                }
              }
            }
          }

          if (changed) {
            seededDescMapRef.current = { ...seededDescMapRef.current, ...newMap };
          }

          return changed ? newMap : prev;
        });
      }
    } else if (!open) {
      prevDescRowIdRef.current = null;
    }
  }, [open, isTypeDesc, activePanelData, rowMappings, customRows]);

  useEffect(() => {
    if (!isTypeDesc || isBulkDescActive || !activePanelData) return;

    const timeoutId = setTimeout(() => {
      flushSave();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [photoDescriptions, isTypeDesc, activePanelData, isBulkDescActive, flushSave]);

  useEffect(() => {
    if (open && !isBulkGenerating) {
      let initialTitle = actualTitle || '';
      if (!initialTitle && rowData) {
        initialTitle = rowData['Title'] || rowData['TITLE'] || rowData['title'] || '';
      }

      const parts = [];
      let hasInfoMappings = false;

      if (rowMappings && customRows) {
        customRows.forEach((cRow) => {
          const mapping = rowMappings[cRow.id] || rowMappings[`prop_${cRow.name}`];
          if (mapping && normalizeString(mapping) === 'info') {
            hasInfoMappings = true;
            if (rowData && rowData[cRow.name]) {
              parts.push(String(rowData[cRow.name]).trim());
            }
          }
        });
      }

      if (!hasInfoMappings && rowData) {
        const systemKeys = [
          'id',
          'lat',
          'lng',
          'matchedFiles',
          'photoMatch',
          '_manuallyAssigned',
          '_localMappings',
        ];
        Object.keys(rowData).forEach((key) => {
          if (!systemKeys.includes(key) && rowData[key]) {
            const lKey = key.toLowerCase();
            if (
              lKey !== 'lat' &&
              lKey !== 'lng' &&
              lKey !== 'latitude' &&
              lKey !== 'longitude'
            ) {
              parts.push(String(rowData[key]).trim());
            }
          }
        });
      }

      let bestSearchTarget = parts.filter(Boolean).join(', ');

      if (!bestSearchTarget) {
        bestSearchTarget = searchTarget || initialTitle;
      }

      if (makeUppercase) {
        initialTitle = initialTitle.toUpperCase();
        bestSearchTarget = bestSearchTarget.toUpperCase();
      }

      setLocalTitle(initialTitle);
      setLocalSearchTarget(bestSearchTarget);

      const { lat: resolvedLat, lng: resolvedLng } = resolveCoordsRobustly(
        rowData,
        initialLat,
        initialLng,
        rowMappings,
        customRows,
      );

      const latVal =
        resolvedLat !== undefined &&
        resolvedLat !== null &&
        resolvedLat !== '' &&
        !isNaN(parseFloat(resolvedLat))
          ? String(parseFloat(resolvedLat).toFixed(10))
          : '';
      const lngVal =
        resolvedLng !== undefined &&
        resolvedLng !== null &&
        resolvedLng !== '' &&
        !isNaN(parseFloat(resolvedLng))
          ? String(parseFloat(resolvedLng).toFixed(10))
          : '';

      setLat(latVal);
      setLng(lngVal);

      if (latVal && lngVal && mapInstanceRef.current && markerRef.current) {
        const pos = { lat: parseFloat(latVal), lng: parseFloat(lngVal) };
        markerRef.current.position = pos;
        mapInstanceRef.current.panTo(pos);
      }

      setErrorMsg('');
      setIsSearching(false);
      setApplyToAll(false);
    }
  }, [
    open,
    searchTarget,
    actualTitle,
    rowData,
    initialLat,
    initialLng,
    makeUppercase,
    rowMappings,
    customRows,
    isBulkGenerating,
  ]);

  useEffect(() => {
    let initTimer;

    if (open && !isTypeDesc) {
      initTimer = setTimeout(() => {
        const { lat: resolvedLat, lng: resolvedLng } = resolveCoordsRobustly(
          rowData,
          initialLat,
          initialLng,
          rowMappings,
          customRows,
        );

        const initLatVal = parseFloat(resolvedLat);
        const initLngVal = parseFloat(resolvedLng);
        const hasValidCoords = !isNaN(initLatVal) && !isNaN(initLngVal);

        const centerLat = hasValidCoords ? initLatVal : viewConfig?.center?.lat || 20;
        const centerLng = hasValidCoords ? initLngVal : viewConfig?.center?.lng || 0;

        const initialZoom =
          userZoomRef.current !== null && userZoomRef.current !== undefined
            ? userZoomRef.current
            : hasValidCoords
              ? 15
              : viewConfig?.zoom || 2;

        const pos = { lat: centerLat, lng: centerLng };

        if (
          mapContainerRef.current &&
          !mapInstanceRef.current &&
          window.google &&
          window.google.maps
        ) {
          try {
            mapInstanceRef.current = new window.google.maps.Map(mapContainerRef.current, {
              center: pos,
              zoom: initialZoom,
              mapId: process.env.REACT_APP_GOOGLE_MAP_ID || 'DEMO_MAP_ID',
              streetViewControl: false,
              mapTypeControl: false,
            });

            mapInstanceRef.current.addListener('zoom_changed', () => {
              if (onZoomChangeRef.current) {
                onZoomChangeRef.current(mapInstanceRef.current.getZoom());
              }
            });

            if (
              window.google.maps.marker &&
              window.google.maps.marker.AdvancedMarkerElement
            ) {
              markerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
                map: mapInstanceRef.current,
                position: pos,
                gmpDraggable: isCoordsActive,
                title: isCoordsActive ? 'Drag to adjust location' : 'Location',
              });

              markerRef.current.addListener('dragend', (event) => {
                const position = event.latLng;
                setLat(position.lat().toFixed(10));
                setLng(position.lng().toFixed(10));
              });
            } else {
              setErrorMsg(
                'AdvancedMarkerElement library is not loaded. Map interactions will be limited.',
              );
            }

            lastRowIdRef.current = rowData?.id;
          } catch (err) {
            console.error('Google Maps Initialization Error:', err);
            setErrorMsg('Failed to initialize map. Check console for details.');
          }
        } else if (mapInstanceRef.current && markerRef.current) {
          const isNewRow = lastRowIdRef.current !== rowData?.id;

          if (isNewRow) {
            lastRowIdRef.current = rowData?.id;
            if (hasValidCoords) {
              mapInstanceRef.current.panTo(pos);
              markerRef.current.position = pos;
            } else {
              const defaultPos = viewConfig?.center || { lat: 20, lng: 0 };
              mapInstanceRef.current.panTo(defaultPos);
              markerRef.current.position = defaultPos;
            }
          } else if (hasValidCoords) {
            mapInstanceRef.current.panTo(pos);
            markerRef.current.position = pos;
          }
        }
      }, 300);
    }

    return () => clearTimeout(initTimer);
  }, [
    open,
    rowData,
    initialLat,
    initialLng,
    isCoordsActive,
    viewConfig,
    isTypeDesc,
    rowMappings,
    customRows,
  ]);

  useEffect(() => {
    if (markerRef.current && !isTypeDesc) {
      markerRef.current.gmpDraggable = isCoordsActive;
      markerRef.current.title = isCoordsActive ? 'Drag to adjust location' : 'Location';
    }
  }, [isCoordsActive, isTypeDesc]);

  const handleResetView = () => {
    if (mapInstanceRef.current && viewConfig) {
      mapInstanceRef.current.panTo(viewConfig.center);
      mapInstanceRef.current.setZoom(viewConfig.zoom);
    }
  };

  const handleCopyLatLng = async () => {
    try {
      if (lat !== '' && lng !== '') {
        const fixedLat = parseFloat(lat);
        const fixedLng = parseFloat(lng);
        if (!isNaN(fixedLat) && !isNaN(fixedLng)) {
          await navigator.clipboard.writeText(
            `${fixedLat.toFixed(10)}, ${fixedLng.toFixed(10)}`,
          );
        }
      }
    } catch (err) {
      console.error('Failed to copy to clipboard', err);
      setErrorMsg('Clipboard access denied. Please copy manually.');
    }
  };

  const handlePasteLatLng = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        const cleanText = text.trim();
        const matches = cleanText.match(/(-?\d+(\.\d+)?)[,\s]+(-?\d+(\.\d+)?)/);

        if (matches && matches.length >= 4) {
          const newLat = parseFloat(matches[1]).toFixed(10);
          const newLng = parseFloat(matches[3]).toFixed(10);
          setLat(newLat);
          setLng(newLng);
          updateMapFromInputs(newLat, newLng);
          setErrorMsg('');
        } else {
          setErrorMsg('Invalid coordinates format. Expected "Lat, Lng".');
        }
      }
    } catch (err) {
      console.error('Failed to read clipboard', err);
      setErrorMsg('Clipboard access denied. Please paste manually.');
    }
  };

  const handleGetLatLng = async () => {
    if (!localSearchTarget) {
      setErrorMsg('Please enter a search target.');
      return;
    }

    if (!window.google || !window.google.maps || !window.google.maps.importLibrary) {
      setErrorMsg('Google Maps API or library infrastructure is not loaded.');
      return;
    }

    setIsSearching(true);
    setErrorMsg('');

    let finalQuery = localSearchTarget.trim();
    if (geoSuffix && geoSuffix.trim() !== '') {
      finalQuery = `${finalQuery}, ${geoSuffix}`;
    }

    try {
      const placesLib = await window.google.maps.importLibrary('places');
      if (!placesLib || !placesLib.Place) {
        throw new Error('Places library could not be resolved.');
      }

      const { places } = await placesLib.Place.searchByText({
        textQuery: finalQuery,
        fields: ['location', 'displayName', 'formattedAddress'],
      });

      setIsSearching(false);

      if (places && places.length > 0) {
        const place = places[0];
        const location = place.location;
        if (location) {
          const newLat = location.lat().toFixed(10);
          const newLng = location.lng().toFixed(10);

          setLat(newLat);
          setLng(newLng);

          if (mapInstanceRef.current && markerRef.current) {
            mapInstanceRef.current.panTo(location);
            markerRef.current.position = location;
          }
        } else {
          setErrorMsg(`No position details returned for top place result.`);
        }
      } else {
        setErrorMsg(`No locations found for "${finalQuery}" matching Places index.`);
      }
    } catch (err) {
      console.error('Places API (New) Query Exception:', err);
      setIsSearching(false);
      setErrorMsg(`Search failed: ${err.message || err}`);
    }
  };

  const handleManualLatChange = (e) => {
    const val = e.target.value;
    setLat(val);
    updateMapFromInputs(val, lng);
  };

  const handleManualLngChange = (e) => {
    const val = e.target.value;
    setLng(val);
    updateMapFromInputs(lat, val);
  };

  const updateMapFromInputs = (currentLat, currentLng) => {
    const pLat = parseFloat(currentLat);
    const pLng = parseFloat(currentLng);

    if (!isNaN(pLat) && !isNaN(pLng) && mapInstanceRef.current && markerRef.current) {
      const pos = { lat: pLat, lng: pLng };
      markerRef.current.position = pos;
      mapInstanceRef.current.panTo(pos);
    }
  };

  const handleConfirm = () => {
    flushSave();
    const parsedLat = parseFloat(lat);
    const parsedLng = parseFloat(lng);

    if (!applyToAll && (isNaN(parsedLat) || isNaN(parsedLng))) {
      setErrorMsg('Please enter valid numerical coordinates before locking in.');
      return;
    }

    const finalLat = !isNaN(parsedLat) ? parsedLat.toFixed(10) : lat;
    const finalLng = !isNaN(parsedLng) ? parsedLng.toFixed(10) : lng;
    const finalTitle = makeUppercase ? localTitle.toUpperCase() : localTitle;
    onConfirm(finalLat, finalLng, applyToAll, finalTitle);

    restoreGridFocus();
  };

  const handleCancelClick = () => {
    if (isBulkGenerating) {
      if (onCancelBulk) onCancelBulk();
      return;
    }

    let baseTitle = actualTitle || '';
    if (!baseTitle && rowData) {
      baseTitle = rowData['Title'] || rowData['TITLE'] || rowData['title'] || '';
    }

    let baseSearch = searchTarget || baseTitle;

    if (makeUppercase) {
      baseTitle = baseTitle.toUpperCase();
      baseSearch = baseSearch.toUpperCase();
    }

    const { lat: resolvedLat, lng: resolvedLng } = resolveCoordsRobustly(
      rowData,
      initialLat,
      initialLng,
      rowMappings,
      customRows,
    );

    const latVal =
      resolvedLat !== undefined &&
      resolvedLat !== null &&
      resolvedLat !== '' &&
      !isNaN(parseFloat(resolvedLat))
        ? String(parseFloat(resolvedLat).toFixed(10))
        : '';
    const lngVal =
      resolvedLng !== undefined &&
      resolvedLng !== null &&
      resolvedLng !== '' &&
      !isNaN(parseFloat(resolvedLng))
        ? String(parseFloat(resolvedLng).toFixed(10))
        : '';

    const isModified =
      lat !== latVal ||
      lng !== lngVal ||
      localTitle !== baseTitle ||
      localSearchTarget !== baseSearch ||
      errorMsg !== '';

    if (isModified) {
      setLat(latVal);
      setLng(lngVal);
      setLocalTitle(baseTitle);
      setLocalSearchTarget(baseSearch);
      setErrorMsg('');
      updateMapFromInputs(latVal, lngVal);

      restoreGridFocus();
    } else {
      handleCancel();
    }
  };

  const handleCancel = () => {
    if (onClose) onClose();
    restoreGridFocus();
  };

  const getPosColName = () => {
    const posColKey = Object.keys(rowMappings || {}).find(
      (k) => rowMappings[k] === 'POSITION (LAT, LNG)' || rowMappings[k] === 'POSITION',
    );
    return posColKey
      ? customRows?.find((r) => r.id === posColKey)?.name ||
          (posColKey.startsWith('prop_') ? posColKey.substring(5) : posColKey)
      : 'POSITION (LAT, LNG)';
  };

  const handleNavPrev = () => {
    flushSave();
    window.dispatchEvent(
      new CustomEvent('loktr-trigger-next-column', {
        detail: { rowId: rowData?.id, colName: getPosColName(), direction: 'prev' },
      }),
    );
  };

  const handleNavNext = () => {
    flushSave();
    window.dispatchEvent(
      new CustomEvent('loktr-trigger-next-column', {
        detail: { rowId: rowData?.id, colName: getPosColName(), direction: 'next' },
      }),
    );
  };

  const handleNavUp = () => {
    flushSave();
    window.dispatchEvent(
      new CustomEvent('loktr-trigger-next-column', {
        detail: { rowId: rowData?.id, colName: getPosColName(), rowDirection: 'up' },
      }),
    );
  };

  const handleNavDown = () => {
    flushSave();
    window.dispatchEvent(
      new CustomEvent('loktr-trigger-next-column', {
        detail: { rowId: rowData?.id, colName: getPosColName(), rowDirection: 'down' },
      }),
    );
  };

  const isOkDisabled = !applyToAll && (isNaN(parseFloat(lat)) || isNaN(parseFloat(lng)));

  useEffect(() => {
    submitRef.current = () => {
      flushSave();
      if (isCoordsActive && !isOkDisabled) {
        handleConfirm();
      }
    };
    cancelRef.current = handleCancelClick;
    navPrevRef.current = handleNavPrev;
    navNextRef.current = handleNavNext;
    navUpRef.current = handleNavUp;
    navDownRef.current = handleNavDown;
  });

  // --- ESCAPE KEY & UNIVERSAL KEYBOARD LISTENER ---
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // EXPLICIT MUTE: Only intercept keys when acting as the Coordinates interface
      // Prevents hijacking grid navigation when serving purely as a map wrapper for other dialogs
      if (!isCoordsActive) return;

      const activeEl = document.activeElement;

      const inSidePanel = !!(activeEl && activeEl.closest('#coordinate-match-panel'));

      const isTextInputReal =
        activeEl &&
        (activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.isContentEditable ||
          (activeEl.closest && activeEl.closest('.ProseMirror')));

      const isControl =
        inSidePanel &&
        (activeEl.tagName === 'BUTTON' ||
          activeEl.type === 'radio' ||
          activeEl.type === 'checkbox' ||
          activeEl.getAttribute('role') === 'combobox' ||
          activeEl.classList?.contains('MuiSelect-select') ||
          activeEl.getAttribute('role') === 'menuitem' ||
          activeEl.getAttribute('role') === 'option' ||
          activeEl.getAttribute('role') === 'switch');

      const isListBox = document.querySelector('[role="listbox"]');

      if (e.key === 'Escape') {
        if (isListBox) return;

        // ESCAPE STEP 1: Blur and go back to Grid
        if (inSidePanel) {
          e.preventDefault();
          e.stopPropagation();
          restoreGridFocus();
          return;
        }

        // ESCAPE STEP 2: Already in DataGrid. Close side panel.
        e.preventDefault();
        e.stopPropagation();
        if (cancelRef.current) cancelRef.current();
        return;
      }

      if (isListBox) {
        if (e.key === 'Enter') enterOriginRef.current = 'listbox';
        return;
      }

      if (!isTextInputReal && !isControl && (e.key === ' ' || e.code === 'Space')) {
        e.preventDefault();
        e.stopPropagation();
        spaceTriggeredRef.current = true;

        const btn = document.getElementById('coords-search-button');
        if (btn && !btn.disabled) {
          btn.click();
        } else {
          let attempts = 0;
          const focusInterval = setInterval(() => {
            const input = document.getElementById('search-target');
            if (input && !input.disabled) {
              input.focus();
              try {
                const len = input.value.length;
                input.setSelectionRange(len, len);
              } catch (err) {}
              clearInterval(focusInterval);
            }
            attempts++;
            if (attempts > 15) clearInterval(focusInterval);
          }, 50);
        }
        return;
      }

      if (isTextInputReal) {
        if (e.key !== 'Enter') return;
      }

      if (e.key === 'Enter') {
        const isComboBox =
          activeEl &&
          (activeEl.getAttribute('role') === 'combobox' ||
            activeEl.classList?.contains('MuiSelect-select'));
        if (isComboBox) return;
        enterOriginRef.current = 'submit';
        e.preventDefault();
        e.stopPropagation();
        setIsOkPressed(true);
        return;
      }

      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();

        // Break the DataGrid trap
        if (activeEl && activeEl.tagName !== 'BODY') {
          activeEl.blur();
        }

        if (e.key === 'ArrowLeft' && navPrevRef.current) navPrevRef.current();
        if (e.key === 'ArrowRight' && navNextRef.current) navNextRef.current();
        if (e.key === 'ArrowUp' && navUpRef.current) navUpRef.current();
        if (e.key === 'ArrowDown' && navDownRef.current) navDownRef.current();
      }
    };

    const handleGlobalKeyUp = (e) => {
      // EXPLICIT MUTE: Only intercept keys when acting as the Coordinates interface
      if (!isCoordsActive) return;

      if (e.key === 'Escape') return; // Handled exclusively in KeyDown

      if (e.key === ' ' || e.code === 'Space') {
        if (spaceTriggeredRef.current) {
          e.preventDefault();
          e.stopPropagation();
          spaceTriggeredRef.current = false;
          return;
        }
      }

      const activeEl = document.activeElement;
      const isTextInputReal =
        activeEl &&
        (activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.isContentEditable ||
          (activeEl.closest && activeEl.closest('.ProseMirror')));

      if (isTextInputReal) return;

      if (e.key === 'Enter') {
        const origin = enterOriginRef.current;
        enterOriginRef.current = null;

        if (origin === 'listbox') {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => {
            if (submitRef.current) submitRef.current();
          }, 150);
          return;
        }

        if (origin !== 'submit') return;

        e.preventDefault();
        e.stopPropagation();
        setIsOkPressed(false);
        if (submitRef.current) submitRef.current();
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown, { capture: true });
    window.addEventListener('keyup', handleGlobalKeyUp, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown, { capture: true });
      window.removeEventListener('keyup', handleGlobalKeyUp, { capture: true });
    };
  }, [isCoordsActive, restoreGridFocus]);

  const handleDragStart = (e, idx) => {
    setDraggedIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', idx);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIdx) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === targetIdx) return;

    const newFiles = [...(rowData?.matchedFiles || [])];
    const [draggedFile] = newFiles.splice(draggedIdx, 1);
    newFiles.splice(targetIdx, 0, draggedFile);

    if (onUpdatePhotos) onUpdatePhotos(newFiles);
    setDraggedIdx(null);
  };

  const handleRemovePhoto = (e, idx) => {
    e.stopPropagation();
    const newFiles = [...(rowData?.matchedFiles || [])];
    newFiles.splice(idx, 1);
    if (onUpdatePhotos) onUpdatePhotos(newFiles);
  };

  return (
    <CoordinateMatchContent
      open={open}
      isTypeDesc={isTypeDesc}
      activePanelData={activePanelData}
      selectedDescPhotoIdx={selectedDescPhotoIdx}
      setSelectedDescPhotoIdx={setSelectedDescPhotoIdx}
      photoDescriptions={photoDescriptions}
      setPhotoDescriptions={setPhotoDescriptions}
      rteFontType={rteFontType}
      rteFontSize={rteFontSize}
      isBulkScraping={isBulkScraping}
      waterfallRef={waterfallRef}
      bulkWaterfallPhotos={bulkWaterfallPhotos}
      isManualMatchActive={isManualMatchActive}
      handleDragStart={handleDragStart}
      handleDragOver={handleDragOver}
      handleDrop={handleDrop}
      handleRemovePhoto={handleRemovePhoto}
      isCoordsActive={isCoordsActive}
      handleResetView={handleResetView}
      viewConfig={viewConfig}
      mapContainerRef={mapContainerRef}
      errorMsg={errorMsg}
      localTitle={localTitle}
      setLocalTitle={setLocalTitle}
      makeUppercase={makeUppercase}
      isBulkGenerating={isBulkGenerating}
      handleConfirm={handleConfirm}
      localSearchTarget={localSearchTarget}
      setLocalSearchTarget={setLocalSearchTarget}
      handleGetLatLng={handleGetLatLng}
      isSearching={isSearching}
      lat={lat}
      handleManualLatChange={handleManualLatChange}
      lng={lng}
      handleManualLngChange={handleManualLngChange}
      handleCopyLatLng={handleCopyLatLng}
      handlePasteLatLng={handlePasteLatLng}
      applyToAll={applyToAll}
      setApplyToAll={setApplyToAll}
      handleCancelClick={handleCancelClick}
      handleNavUp={handleNavUp}
      handleNavDown={handleNavDown}
      handleNavPrev={handleNavPrev}
      handleNavNext={handleNavNext}
      isOkDisabled={isOkDisabled}
      isOkPressed={isOkPressed}
      children={children}
    />
  );
}
